import { useDashboardData } from "../../../context/DashboardContext";
import { ResponsivePie } from "@nivo/pie";
import { useActiveTheme } from "../../../context/ThemeContext";
import type { WidgetComponentProps } from "../types";

interface ChartDataItem {
  name: string;
  value: number;
  percentage?: number;
  revenue?: number;
}

const toNivoPieData = (data: ChartDataItem[], colors: string[]) => {
  const mergedMap = new Map<string, { value: number; revenue: number }>();
  for (const item of data) {
    const existing = mergedMap.get(item.name);
    if (existing) {
      existing.value += item.value;
      existing.revenue += (item.revenue || 0);
    } else {
      mergedMap.set(item.name, { value: item.value, revenue: item.revenue || 0 });
    }
  }

  const merged = Array.from(mergedMap.entries()).map(([name, d]) => ({
    name, value: d.value, revenue: d.revenue,
  }));

  merged.sort((a, b) => b.value !== a.value ? b.value - a.value : a.name.localeCompare(b.name));

  const total = merged.reduce((sum, item) => sum + item.value, 0);

  return merged.map((item, index) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
};

const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: datum.color }} />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

export default function PieChartWidget({ config, title }: WidgetComponentProps) {
  const PIE_COLORS = useActiveTheme().colors.chart;
  const { chartData, chartInteractions } = useDashboardData();

  const dataKey = config.dataKey as string;
  const height = (config.height as number) || 350;
  const marginX = (config.marginX as number) || 120;
  const marginY = (config.marginY as number) || 40;
  const colors = (config.colors as string[] | undefined) || PIE_COLORS;

  const rawData = (chartData[dataKey] as ChartDataItem[] | undefined) || [];
  const pieData = toNivoPieData(rawData, colors);
  const interaction = chartInteractions?.[dataKey];

  if (pieData.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col">
        <h3 className="text-lg font-semibold mb-4">{title}</h3>
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col overflow-hidden">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <div className="flex-1" style={{ minHeight: height }}>
        <ResponsivePie
          data={pieData}
          margin={{ top: marginY, right: marginX, bottom: marginY, left: marginX }}
          innerRadius={0.5}
          padAngle={1}
          cornerRadius={3}
          activeOuterRadiusOffset={8}
          sortByValue={false}
          colors={{ datum: "data.color" }}
          borderWidth={2}
          borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
          enableArcLinkLabels={true}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          arcLinkLabel={(d) => {
            const label = String(d.label);
            return label.length > 25 ? label.slice(0, 22) + "..." : label;
          }}
          enableArcLabels={true}
          arcLabelsSkipAngle={10}
          arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
          arcLabelsTextColor="white"
          theme={getNivoPieTheme()}
          tooltip={PieChartTooltip}
          onClick={interaction ? (datum) => interaction.onSelect(String(datum.label)) : undefined}
          isInteractive={Boolean(interaction)}
          legends={[]}
        />
      </div>
    </div>
  );
}
